import {
  Database,
  GraduationCap,
  Settings,
  Code,
  Smartphone,
  Brain,
  Clock,
  BarChart3,
  Target,
  CreditCard,
  Car,
  FileText,
  Server,
  Cloud,
  Layers,
  User,
} from "lucide-react";
// NOTE: Use public paths for PNGs to avoid build-time image processing (sharp) on some Linux servers
const tendering = "/assets/tender.png";
const diwan = "/assets/diwan.png";

import presidential from "@/assets/presidential.svg";
import oman from "@/assets/Oman.svg";
import tanmiah from "@/assets/Tanmiah.svg";
import adaa from "@/assets/Adaa.svg";
import reyad from "@/assets/reyad.svg";
import seha from "@/assets/Seha.svg";
import logo from "@/assets/USA.svg";
const auditAppUrl =
  process.env.NEXT_PUBLIC_AUDIT_APP_URL || "https://audit.orapex.com";
type NavItem = {
  name: string;
  href: string;
  target?: string;
  badge?: string;
};
export const navItems: NavItem[] = [
  { name: "Home", href: "#hero" },
  { name: "Partners", href: "#partners" },
  { name: "Products", href: "#products" },
  { name: "Services", href: "/services" },
  { name: "About Us", href: "#about-us" },
  // { name: 'AI Code Audit', href: auditAppUrl, target: '_blank', badge: 'Beta' },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "#contact" },
  {
    name: "iTimePlus Login",
    href: "https://itimeplus.orapex.com/ords/f?p=calp:101",
    target: "_blank",
  },
];

export const staticMessages = [
  {
    id: 1,
    type: "client",
    text: "Hi, I need advanced solutions for my business.",
  },
  {
    id: 2,
    type: "bot",
    text: "Absolutely! We specialize in AI-driven solutions, Oracle APEX, full-stack & mobile development, and training across the USA and Middle East.",
  },
  {
    id: 3,
    type: "client",
    text: "Great! How can I contact you?",
  },
];
export { services, overviewServices, servicesOverview } from "./services";
export type { Service, OverviewService } from "./services";

export const erpContent = {
  hero: {
    eyebrow: "ERP Built Around Your Business",
    headline: "Less Friction.",
    highlight: "More Control",
    primaryCta: "Request an ERPNext Demo",
    secondaryCta: "Explore Our Services",
  },
  solutions: {
    heading: "Comprehensive",
    highlight: "ERP Solutions",
    intro:
      "A complete range of ERPNext services where business needs meet technical expertise, and operational challenges turn into efficient workflows.",
    cta: "Discuss Your ERPNext Requirements",
    items: [
      {
        icon: "Rocket",
        title: "ERPNext Implementation",
        description:
          "Set up ERPNext according to your business structure, operational processes, and required modules.",
      },
      {
        icon: "Compass",
        title: "ERP Consulting",
        description:
          "Assess your business requirements and identify the appropriate system setup, modules, and implementation scope.",
      },
      {
        icon: "SlidersHorizontal",
        title: "ERP Customization",
        description:
          "Customize forms, fields, workflows, user permissions, and reports to match your specific business requirements.",
      },
      {
        icon: "Code",
        title: "ERPNext Development",
        description:
          "Develop additional features and applications to extend standard ERPNext functionality and address specific business needs.",
      },
      {
        icon: "Plug",
        title: "ERP Integration",
        description:
          "Connect ERP with compatible third-party platforms, APIs, and business applications to streamline data exchange and connect business processes.",
      },
    ],
  },
  integration: {
    heading: "ERP",
    highlight: "Integration & Connected Systems",
    subtitle: "One Unified System. Complete Business Clarity",
    description:
      "Build a dynamic business environment that works around your existing business applications, linking data and processes across sales, finance, inventory, and external platforms.",
    benefits: [
      "Seamless Data Exchange",
      "Reduced Manual Data Entry",
      "Efficient Business Workflows",
    ],
  },
  industries: {
    heading: "ERP Solutions Adapted to",
    highlight: "Your Industry",
    cta: "Discuss Your Industry Requirements",
    items: [
      {
        icon: "HeartPulse",
        title: "Healthcare",
        description:
          "Configure administrative workflows, appointments, billing, and related operations for healthcare organizations.",
      },
      {
        icon: "Factory",
        title: "Manufacturing",
        description:
          "Coordinate production planning, material requirements, stock levels, and manufacturing operations in one system.",
      },
      {
        icon: "Store",
        title: "Retail & Distribution",
        description:
          "Manage sales orders, purchasing, inventory, and warehouse operations in one connected system.",
      },
      {
        icon: "HardHat",
        title: "Construction & Contracting",
        description:
          "Track project expenses, purchasing, materials, and costs across construction operations.",
      },
      {
        icon: "Briefcase",
        title: "Professional Services",
        description:
          "Align project management, time tracking, expenses, client records, and billing to organize service delivery.",
      },
    ],
  },
  process: {
    eyebrow: "Implementation Roadmap",
    heading: "How We Deliver",
    highlight: "ERP Solutions",
    description:
      "A structured implementation process ensures ERP is configured to meet your business requirements, from initial planning to system launch.",
    footnote: "4 steps, from analysis to go-live",
    steps: [
      {
        title: "Business Requirements Analysis",
        description:
          "Assess business objectives, existing workflows, and operational requirements to define the project scope.",
      },
      {
        title: "System Configuration and Data Migration",
        description:
          "Configure relevant modules, company settings, user roles, and permissions, while preparing and migrating existing business data.",
      },
      {
        title: "Integration, Testing and User Training",
        description:
          "Integrate external applications, test system functionality and data accuracy, and train users before launch.",
      },
      {
        title: "System Launch and Ongoing Support",
        description:
          "Support the system launch and daily operations, with ongoing technical support and maintenance based on the agreed scope.",
      },
    ],
  },
};

export const projects = [
  {
    title: "Custom Tendering System",
    client: "Gulf Health Council",
    description:
      "Streamlined procurement and tendering process with automated workflows and real-time tracking",
    category: "Enterprise System",
    tech: ["Oracle APEX", "PL/SQL", "REST APIs"],
    results: "70% faster processing, 90% reduction in errors",
    image: tendering,
  },
  {
    title: "CMS UI/UX Modernization",
    client: "Diwan Project",
    description:
      "Complete redesign of content management system with modern interface and improved user experience",
    category: "UI/UX Design",
    tech: ["React", "TypeScript", "Tailwind CSS"],
    results:
      "60% improvement in user satisfaction, 40% faster content creation",
    image: diwan,
  },
  {
    title: "Instant Friday Sermon Translation",
    client: "Muscat Apps",
    description:
      "Real-time translation system for religious content distribution across multiple languages",
    category: "Mobile App",
    tech: ["Flutter", "AI Translation", "Cloud Functions"],
    results: "500K+ users, 15 languages supported",
    image:
      "https://images.unsplash.com/photo-1627542557169-5ed71c66ed85?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2JpbGUlMjBhcHAlMjBkZXZlbG9wbWVudCUyMHNjcmVlbnN8ZW58MXx8fHwxNzU1NjcyNjE3fDA&ixlib=rb-4.1.0&q=80&w=1080",
  },
  {
    title: "Automated Power BI Reporting",
    client: "Gulf Health Council",
    description:
      "Intelligent reporting system with automated data visualization and executive dashboards",
    category: "BI & Analytics",
    tech: ["Power BI", "Azure Functions", "SQL Server"],
    results: "80% reduction in manual reporting, real-time insights",
    image:
      "https://images.unsplash.com/photo-1575388902449-6bca946ad549?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZGFzaGJvYXJkJTIwYW5hbHl0aWNzJTIwc2NyZWVufGVufDF8fHx8MTc1NTc3NjQ3MXww&ixlib=rb-4.1.0&q=80&w=1080",
  },
];

export const products = [
  {
    icon: "Clock",
    title: "iTimePlus",
    subtitle: "Time & Cost Management",
    description:
      "Advanced project time tracking and cost management solution with AI-powered insights",
    features: [
      "AI Time Tracking",
      "Cost Forecasting",
      "Resource Planning",
      "Custom Reports",
    ],
  },
  {
    icon: "FileText",
    title: "Smart Contract Manager",
    subtitle: "Compliance Made Easy",
    description:
      "Automated contract lifecycle management and compliance tracking with AI analysis",
    features: [
      "Contract Analysis",
      "Compliance Alerts",
      "Renewal Tracking",
      "Risk Assessment",
    ],
  },
  {
    icon: "CreditCard",
    title: "KA$H AI",
    subtitle: "AI Receipt Analysis. See Every Expense Instantly",
    description:
      "Extract receipt data in real time with an AI system built for accountants, Smart Approval & QuickBooks Integration.",
    features: [
      "Receipt Scanning",
      "Fraud Detection",
      "Budget Planning",
      "Expense Analytics",
    ],
  },
  {
    icon: "BarChart3",
    title: "PRiSM",
    subtitle: "From Insight to Action",
    description:
      "Turn live data into decisions. See operations in real time, forecast outcomes, and automate report delivery to your existing tools.",
    features: [
      "Real-time Dashboards",
      "Predictive Analytics",
      "Automated Insights",
      "Data Visualization",
    ],
  },
  {
    icon: "Car",
    title: "Lavender",
    subtitle: "AI Transportation Management",
    description:
      "Intelligent fleet management and logistics optimization with predictive maintenance",
    features: [
      "Route Optimization",
      "Predictive Maintenance",
      "Driver Analytics",
      "Fuel Management",
    ],
  },
];

export const stats = [
  { number: 150, label: "Projects Completed", suffix: "+" },
  { number: "3X Faster", label: "Time to Market", suffix: "" },
  { number: 50, label: "Happy Clients", suffix: "+" },
  { number: "24/7", label: "Technical Support", suffix: "" },
];

export const testimonials = [
  {
    name: "Ahmed Al-Mansouri",
    role: "CTO, Gulf Health Council",
    company: "Gulf Health Council",
    content:
      "ORAPEX transformed our procurement system completely. The automated tendering system they developed has saved us countless hours and significantly reduced processing errors. Their team's expertise in Oracle APEX is unmatched.",
    avatar:
      "https://images.unsplash.com/photo-1584940121730-93ffb8aa88b0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHBlcnNvbiUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDF8fHx8MTc1NTY3NTkxN3ww&ixlib=rb-4.1.0&q=80&w=1080",
    rating: 5,
  },
  {
    name: "Sarah Johnson",
    role: "Project Manager, Diwan",
    company: "Diwan Project",
    content:
      "The UI/UX modernization project exceeded our expectations. The new interface is intuitive and our content creators are 40% more productive. ORAPEX delivered on time and within budget.",
    avatar:
      "https://images.unsplash.com/photo-1584940121730-93ffb8aa88b0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHBlcnNvbiUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDF8fHx8MTc1NTY3NTkxN3ww&ixlib=rb-4.1.0&q=80&w=1080",
    rating: 5,
  },
  {
    name: "Dr. Omar Al-Rashid",
    role: "Director, Muscat Apps",
    company: "Muscat Apps",
    content:
      "The instant translation app has reached over 500,000 users across 15 languages. ORAPEX's AI-driven solution has made religious content accessible to Muslims worldwide. Exceptional work!",
    avatar:
      "https://images.unsplash.com/photo-1584940121730-93ffb8aa88b0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHBlcnNvbiUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDF8fHx8MTc1NTY3NTkxN3ww&ixlib=rb-4.1.0&q=80&w=1080",
    rating: 5,
  },
];

export const technologies = [
  {
    name: "Oracle APEX",
    icon: Database,
    category: "Database",
    description: "Low-code enterprise application development platform",
    expertiseLevel: "95%",
    expertise: "Expert",
    benefits: ["Rapid Development", "Enterprise Ready", "Highly Secure"],
  },
  {
    name: "React/Next.js",
    icon: Code,
    category: "Frontend",
    description: "Modern frontend frameworks for building interactive UIs",
    expertiseLevel: "92%",
    expertise: "Expert",
    benefits: ["Component Based", "SEO Friendly", "High Performance"],
  },
  {
    name: "Node.js",
    icon: Server,
    category: "Backend",
    description:
      "JavaScript runtime for building scalable backend applications",
    expertiseLevel: "88%",
    expertise: "Advanced",
    benefits: ["Fast Development", "Scalable", "Large Ecosystem"],
  },
  {
    name: "Python/AI/ML",
    icon: Brain,
    category: "AI/ML",
    description: "Machine learning and artificial intelligence solutions",
    expertiseLevel: "90%",
    expertise: "Expert",
    benefits: ["AI Capabilities", "Data Science", "Automation"],
  },
  {
    name: "AWS/Azure",
    icon: Cloud,
    category: "Cloud",
    description: "Cloud infrastructure and services for scalable applications",
    expertiseLevel: "85%",
    expertise: "Advanced",
    benefits: ["Auto Scaling", "Global Reach", "Cost Effective"],
  },
  {
    name: "Docker/K8s",
    icon: Layers,
    category: "DevOps",
    description: "Containerization and orchestration technologies",
    expertiseLevel: "82%",
    expertise: "Advanced",
    benefits: ["Portable", "Efficient", "Scalable"],
  },
  {
    name: "Flutter/Mobile",
    icon: Smartphone,
    category: "Mobile",
    description: "Cross-platform mobile application development",
    expertiseLevel: "87%",
    expertise: "Advanced",
    benefits: ["Cross Platform", "Native Performance", "Single Codebase"],
  },
  {
    name: "Power BI/Analytics",
    icon: BarChart3,
    category: "Analytics",
    description: "Business intelligence and data visualization tools",
    expertiseLevel: "83%",
    expertise: "Advanced",
    benefits: ["Data Insights", "Interactive Reports", "Real-time Analytics"],
  },
  {
    name: "PostgreSQL",
    icon: Database,
    category: "Database",
    description: "Advanced open-source relational database system",
    expertiseLevel: "89%",
    expertise: "Expert",
    benefits: ["ACID Compliant", "Extensible", "Standards Compliant"],
  },
  {
    name: "MongoDB",
    icon: Database,
    category: "Database",
    description: "NoSQL document database for modern applications",
    expertiseLevel: "86%",
    expertise: "Advanced",
    benefits: ["Flexible Schema", "Horizontal Scaling", "Rich Queries"],
  },
  {
    name: "Vue.js",
    icon: Code,
    category: "Frontend",
    description: "Progressive JavaScript framework for building UIs",
    expertiseLevel: "84%",
    expertise: "Advanced",
    benefits: ["Easy Learning", "Flexible", "Performance"],
  },
  {
    name: "TypeScript",
    icon: Code,
    category: "Frontend",
    description: "Typed superset of JavaScript for better code quality",
    expertiseLevel: "91%",
    expertise: "Expert",
    benefits: ["Type Safety", "Better IDE Support", "Refactoring"],
  },
  {
    name: "GraphQL",
    icon: Server,
    category: "Backend",
    description: "Query language and runtime for APIs",
    expertiseLevel: "81%",
    expertise: "Advanced",
    benefits: ["Flexible Queries", "Type System", "Single Endpoint"],
  },
  {
    name: "Redis",
    icon: Server,
    category: "Backend",
    description: "In-memory data structure store for caching",
    expertiseLevel: "85%",
    expertise: "Advanced",
    benefits: ["High Performance", "Data Structures", "Persistence"],
  },
  {
    name: "Terraform",
    icon: Cloud,
    category: "Cloud",
    description: "Infrastructure as code tool for cloud resources",
    expertiseLevel: "80%",
    expertise: "Intermediate",
    benefits: ["Infrastructure as Code", "Multi-cloud", "Version Control"],
  },
  {
    name: "Jenkins",
    icon: Layers,
    category: "DevOps",
    description: "Continuous integration and deployment automation",
    expertiseLevel: "83%",
    expertise: "Advanced",
    benefits: ["Automation", "Extensible", "Open Source"],
  },
];

export const locations = [
  { name: "USA", x: "20%", y: "35%" },
  { name: "Jordan", x: "55%", y: "40%" },
  { name: "Saudi Arabia", x: "58%", y: "50%" },
  { name: "Oman", x: "62%", y: "55%" },
  { name: "Dubai", x: "60%", y: "52%" },
];

// Partners/Companies we worked with
export const partners = [
  {
    name: "Oman",
    logo: oman.src,
    // category: "Technology Partner",
  },

  {
    name: "Presidential court",
    logo: presidential.src,
    // category: "Cloud Partner",
  },
  {
    name: "Adaa",
    logo: adaa.src,
    // category: "Enterprise Client",
  },
  {
    name: "Tanmiah",
    logo: tanmiah.src,
    // category: "Enterprise Client",
  },
  {
    name: "Riyadh Development Company",
    logo: reyad.src,
    // category: "Enterprise Client",
  },
  {
    name: "Seha",
    logo: seha.src,
    // category: "Enterprise Client",
  },
  // {
  //   name: "Department of State",
  //   logo: logo.src,
  //   // category: "Enterprise Client",

  // },
];
